import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router';

import { MembershipDTO, UserDTO } from '@invenco/accounts-interface';
import { AccessContext } from '@invenco/common-interface/auth';

import { getMembershipName } from '@/components/navigation-sidebar/account-menu/membership-helpers';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useCurrentUserQuery } from '@/shared/hooks/queries/common';
import { useMessages } from '@/shared/providers/MessagesProvider';

export type SelectMembershipVariables = {
  membershipId: string;
  redirectTo?: string;
  redirectOptions?: NavigateOptions;
};

export function useMemberships() {
  const navigate = useNavigate();
  const messages = useMessages();
  const { accountsGateway } = useGateways();
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { data: { memberships, activeMembership } = {}, isLoading } = useCurrentUserQuery();

  const [changingToMembershipId, setChangingToMembershipId] = useState<string>();

  const membershipGroups = useMemo(() => {
    const membershipsByContext = (memberships ?? []).reduce((acc, m) => {
      if (!acc.has(m.accessContext)) {
        acc.set(m.accessContext, []);
      }
      acc.get(m.accessContext)!.push(m);
      return acc;
    }, new Map<AccessContext, MembershipDTO[]>());
    return [AccessContext.PLATFORM, AccessContext.SELLER, AccessContext.WAREHOUSE]
      .map((context) => ({
        context,
        memberships: membershipsByContext.get(context) ?? [],
      }))
      .filter((g) => g.memberships.length > 0);
  }, [memberships]);

  const { mutate: selectMembership } = useGatewayMutation<UserDTO, SelectMembershipVariables>({
    onMutate: async ({ membershipId }) => {
      setChangingToMembershipId(membershipId);
      await queryClient.cancelQueries();
    },
    mutationFn: ({ membershipId }) => accountsGateway.setUserActiveMembership(membershipId),
    onSuccess: async (newUser, { redirectTo, redirectOptions }) => {
      // force a refresh of the auth0 token
      await getAccessTokenSilently({ cacheMode: 'off' });

      // delay to allow routing changes to propagate after token is updated
      setTimeout(async () => {
        // cancel again as new queries may have started since the mutation began
        await queryClient.cancelQueries();

        // IMPORTANT - the cache must be cleared as query keys don't include account/membership id
        // if this is not done, initially cached data from the wrong account may be returned from queries.
        // We're doing it upon navigation and not immediately to prevent attempting to immediately refetch
        // data on screen with the wrong token.
        queryClient.clear();

        // Reset the user query data to prevent it flashing the loading state again after clearing cache
        queryClient.setQueryData(['users', 'me'], newUser);

        // navigate to the requested destination (or home as routes may change)
        void navigate(redirectTo ?? '/', redirectOptions);

        setChangingToMembershipId(undefined);

        messages.info('Switched account', {
          name: getMembershipName(newUser?.activeMembership ?? undefined),
        });
      }, 100);
    },
    onError: () => setChangingToMembershipId(undefined),
    linkedQuery: ['users', 'me'],
  });

  return {
    isLoading,
    activeMembership: activeMembership ?? undefined,
    changingToMembership: changingToMembershipId
      ? memberships?.find((m) => m.id === changingToMembershipId)
      : undefined,
    hasMultipleMemberships: !!memberships && memberships.length > 1,
    membershipGroups,
    selectMembership,
  };
}
